import { type MetricConfiguration } from "@tokenterminal/tt-analytics-api-types/dist/api/metrics"
import {
  type ColDef,
  type ColGroupDef,
  type SetFilterValuesFunc,
  type ColumnState,
} from "modules/datatable/DataGrid"
import { type MartPreset } from "modules/datatable/types"
import { type MartColumn, type MartId } from "types/ApiTypes"
import { capitalize } from "utils/lodash/capitalize"
import { type MartData, getAllAvailableValuesFromField } from "./datasource"

const FILTER_PARAM_NEEDING_DATA_TYPES: Array<ColDef["cellDataType"]> = [
  "collection",
  "project",
  "chain",
  "marketSector",
]

type InitialColumnState = Partial<ColDef>

// Keys not to be transformed from "foo" into "initialFoo"
const nonInitializedKeys: Array<keyof ColumnState> = ["width", "flex"]
// Keys of preset's columns to be omitted from initial column state
const omitFromInitialKeys: Array<keyof ColumnState> = ["colId"]

const prepareColumnPreset = (col: ColumnState): InitialColumnState => {
  const keys = Object.keys(col) as Array<keyof ColumnState>
  const column: Record<string, unknown> = {}

  for (const k of keys) {
    const value = col[k]
    if (
      value !== null &&
      typeof value !== "undefined" &&
      !omitFromInitialKeys.includes(k)
    ) {
      // Make keys such as "hide" and "pivot" into "initialHide" and "initialPivot"
      const resolvedKey =
        nonInitializedKeys.includes(k) || k.startsWith("initial")
          ? k
          : `initial${capitalize(k)}`
      column[resolvedKey] = value
    }
  }
  return column
}

// Same as Admin UI Mart management uses
const FIELD_REGEX =
  /(.*)_((?:\d+)(?:h|d)|lifetime|max|all)(?:_(?:month|week|day|hour))?_(\w+)$/

const getColumnMetric = (field: string) => {
  const [, columnMetric] = field.match(FIELD_REGEX) || []
  return columnMetric
}

const resolveSingleFlatColumn = (
  martCol: MartColumn,
  martId: MartId,
  metricConfigurations: Array<MetricConfiguration>,
  presetState?: InitialColumnState
): ColDef => {
  // Omit invalid ColDef props
  const {
    // DESCTRUCT ONLY
    col_id,
    parent,
    updated_at,
    created_at,
    enabled,
    column_index,
    // Convert to use
    header_tooltip,
    enable_value_row,
    enable_row_group,
    cell_data_type,
    header_name,
    ...nativeCol
  } = martCol

  const col: ColDef & typeof nativeCol = Object.assign(
    {},
    nativeCol,
    presetState
  )

  // If we want to, we could also use updated_at in tooltip for transparency
  col.cellDataType = cell_data_type
  col.headerName = header_name
  const columnMetric = getColumnMetric(col.field)

  if (columnMetric) {
    // Resolve properties based on metric information. Dont send metric info to UI
    const metricConfig = metricConfigurations.find(
      (conf) => conf.metric_id === columnMetric
    )

    if (col.cellDataType === "trend" && metricConfig?.is_lower_value_better) {
      // Auto-resolve as inverted trend in case dataset defaulted to trend. This way we dont leak metric logic into UI
      col.cellDataType = "trendInverse"
    }
  }

  // Optional
  col.headerTooltip = header_tooltip ?? undefined
  col.enableValue = enable_value_row ?? false
  col.enableRowGroup = enable_row_group ?? false

  if (FILTER_PARAM_NEEDING_DATA_TYPES.includes(col.cellDataType)) {
    col.filterParams = {
      async values(
        params: Parameters<SetFilterValuesFunc<MartData, string | number>>[0]
      ) {
        const data = await getAllAvailableValuesFromField(
          martId,
          col.field,
          header_name
        )
        params.success(data)
      },
    }
  }

  return col
}

const rowIndexColumn: ColDef = {
  headerName: "#",
  type: "rowIndexColumn",
  chartDataType: "excluded",
}

export const resolveColDefs = (
  columns: MartColumn[],
  martId: MartId,
  defaultPreset: MartPreset | null = null,
  metricConfigurations: Array<MetricConfiguration>
): (ColDef | ColGroupDef)[] => {
  // ColDefs are what this fn. returns in the end
  const colDefs: (ColDef | ColGroupDef)[] = []

  // Groups to be gathered from columns -data
  const groupsMap = new Map<string, ColGroupDef>()

  // Default preset columns' map to be used when resolving column definition
  const presetColumnsMap = new Map<string, InitialColumnState>()

  if (defaultPreset) {
    // AG Grid uses auto cols for example grouping. In preset they default information about default state that needs to be passed to cols
    const presetAutoCols = new Map<string, ColumnState>()

    for (let col of defaultPreset.config.columns) {
      if (col.colId.includes("ag-Grid-AutoColumn-")) {
        // If auto col, store for later use. Auto columns appear to be always first in columns, so should be safe logic
        presetAutoCols.set(col.colId.replace("ag-Grid-AutoColumn-", ""), col)
      }

      if (presetAutoCols.has(col.colId)) {
        // Merge col with auto col's props, but only take the ones that do not exist in the column itself. This keeps eg. colId the same, but passes eg. sort if that's set on auto col (group col)
        col = {
          ...presetAutoCols.get(col.colId),
          ...col,
        }
      }
      // Create map of column specific default preset additions. Basically merges the default preset into the column definitions for the initial load.
      presetColumnsMap.set(col.colId, prepareColumnPreset(col))
    }
  }

  /*
   * We assume API doesn't always return groups before their children.
   * That however is necessity for the logic that follows this,
   * so therefor we sort all groups as first here
   */
  columns.sort((a, b) => {
    // If a column doesn't have a "field", it's a group column
    if (!a.field) {
      return !b.field ? 0 : -1
    }
    return b.field ? 0 : 1
  })

  /*
   * Loop groups and cols with the assumption that group columns come first
   */
  for (const martColumn of columns) {
    const { field, header_tooltip, col_id, parent, header_name } = martColumn

    if (!field) {
      const groupCol: ColGroupDef = {
        headerName: header_name,
        groupId: col_id,
        children: [],
        headerTooltip: header_tooltip ?? undefined,
      }

      groupsMap.set(col_id, groupCol)
      colDefs.push(groupCol)
    } else if (parent) {
      // Aggregate column like Price 30d avg
      const group = groupsMap.get(parent)
      group?.children.push(
        resolveSingleFlatColumn(
          martColumn,
          martId,
          metricConfigurations,
          presetColumnsMap.get(martColumn.col_id)
        )
      )
    } else {
      // No parent so already a flat column like eg. "Project name"
      colDefs.push(
        resolveSingleFlatColumn(
          martColumn,
          martId,
          metricConfigurations,
          presetColumnsMap.get(martColumn.col_id)
        )
      )
    }
  }

  const sortByColIds: string[] = Array.from(presetColumnsMap.entries())
    // Filter out grouped rows because they are not in correct place in order
    .filter(([_, { rowGroup }]) => !rowGroup)
    // AG Grid creates auto cols from the grouped columns and they are in correct place in order, so use those in place of rowGrouped ones
    .map(([field]) => field.replace("ag-Grid-AutoColumn-", ""))

  // Sort columns inside groupColumns
  for (const def of colDefs) {
    if ((def as ColGroupDef).children?.length) {
      ;(def as ColGroupDef).children.sort((a, b) => {
        const aId = ((a as ColGroupDef).groupId ??
          (a as ColDef).field)!.replace("_group", "")
        const bId = ((b as ColGroupDef).groupId ??
          (b as ColDef).field)!.replace("_group", "")
        return sortByColIds.indexOf(aId) - sortByColIds.indexOf(bId)
      })
    }
  }

  // Sort columns by default preset's order
  colDefs.sort((a, b) => {
    // Id is either group's id or field (group doesn't have field, but they do have postfix _group, replace that)
    const aId = ((a as ColGroupDef).groupId ?? (a as ColDef).field)!.replace(
      "_group",
      ""
    )
    const bId = ((b as ColGroupDef).groupId ?? (b as ColDef).field)!.replace(
      "_group",
      ""
    )

    /*
     * Sort cols are flat and dont have groups. All columns here should be found from the haystack, if not
     * they are group columns. Need to look for with startsWith eg.
     * "transfer_count" (is a group) => look for: "transfer_count_1d_something"'s position
     */
    let aIndex = sortByColIds.indexOf(aId)
    if (aIndex === -1) {
      // Need to revise id
      aIndex = sortByColIds.findIndex((col) => col.startsWith(aId))
    }

    let bIndex = sortByColIds.indexOf(bId)

    if (bIndex === -1) {
      // Need to revise id
      bIndex = sortByColIds.findIndex((col) => col.startsWith(bId))
    }

    if (aIndex === -1 || bIndex === -1) {
      if (process.env.NODE_ENV === "development") {
        throw "Sorting failed, wut?"
      } else {
        console.warn("Column sorting compromised")
      }
    }

    return aIndex - bIndex
  })

  // Put static/hardcoded columns as last
  colDefs.unshift(rowIndexColumn)

  return colDefs
}
